import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Zap } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const fieldCls = "bg-[#0C0B08] border-[#D4A72C]/25 text-slate-100 h-11";
const todayRome = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });

const Lbl = ({ children }) => <div className="text-[11px] uppercase tracking-[0.18em] text-[#C9A961] mb-1.5">{children}</div>;

export function EvChargeForm({ charge, drivers, vehicles, onClose }) {
  const [f, setF] = useState({ date: charge?.date || todayRome(), driver_id: charge?.driver_id || "", vehicle_id: charge?.vehicle_id || "",
    amount: charge?.amount ?? "", note: charge?.note || "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const veh = vehicles.find((v) => v.id === f.vehicle_id);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { if (charge && !vehicles.find((v) => v.id === charge.vehicle_id)) toast.warning("Il veicolo di questa ricarica non è più segnato come elettrico in Flotta"); }, [charge, vehicles]);

  const save = async () => {
    const amount = Number(String(f.amount).replace(",", "."));
    if (!f.date || !f.driver_id || !f.vehicle_id || !(amount > 0)) { toast.error("Compila data, autista, veicolo e importo"); return; }
    setBusy(true);
    try {
      const body = { date: f.date, driver_id: f.driver_id, vehicle_id: f.vehicle_id, amount, note: f.note || null };
      const r = charge ? await api.patch(`/ev-charges/${charge.id}`, body) : await api.post("/ev-charges", body);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        try { await api.post(`/ev-charges/${r.data.id}/receipt`, fd); }
        catch (e) { toast.error(`Ricarica salvata, ma la ricevuta non è stata caricata: ${formatApiError(e)}`); onClose(true); return; }
      }
      toast.success(charge ? "Ricarica aggiornata" : "Ricarica registrata");
      onClose(true);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-lg bg-[#070605] border-[#D4A72C]/30 text-slate-200" data-testid="ev-form">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white inline-flex items-center gap-2"><Zap className="text-[#E9C46A]" size={22} />{charge ? "Modifica ricarica" : "Nuova ricarica"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><Lbl>Data</Lbl><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className={fieldCls} data-testid="ev-form-date" /></div>
          <div><Lbl>Importo (€)</Lbl><Input inputMode="decimal" value={f.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0,00" className={fieldCls} data-testid="ev-form-amount" /></div>
          <div className="col-span-2"><Lbl>Autista</Lbl>
            <Select value={f.driver_id} onValueChange={(v) => set("driver_id", v)}>
              <SelectTrigger className={fieldCls} data-testid="ev-form-driver"><SelectValue placeholder="Seleziona autista" /></SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800 max-h-72">{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Lbl>Veicolo elettrico</Lbl>
            <Select value={f.vehicle_id} onValueChange={(v) => set("vehicle_id", v)}>
              <SelectTrigger className={fieldCls} data-testid="ev-form-vehicle"><SelectValue placeholder={vehicles.length ? "Seleziona veicolo" : "Nessun veicolo segnato «Elettrico» in Flotta"} /></SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800">{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.label} · {v.plate}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Lbl>Targa</Lbl><div className="h-11 rounded-md border border-white/10 bg-white/[0.03] px-3 flex items-center font-mono-num text-[#E9C46A]" data-testid="ev-form-plate">{veh?.plate || charge?.plate || "—"}</div></div>
          <div className="col-span-2"><Lbl>Nota (facoltativa)</Lbl><Textarea rows={2} value={f.note} onChange={(e) => set("note", e.target.value)} placeholder="Es. Supercharger Roma" className="bg-[#0C0B08] border-[#D4A72C]/25" data-testid="ev-form-note" /></div>
          <div className="col-span-2"><Lbl>Ricevuta (facoltativa)</Lbl>
            <label className="flex items-center gap-2 h-11 px-3 rounded-md border border-dashed border-[#D4A72C]/40 text-sm text-slate-300 cursor-pointer hover:bg-[#D4A72C]/5">
              <Paperclip size={15} className="text-[#E9C46A]" />
              <span className="truncate">{file ? file.name : charge?.has_receipt ? `Sostituisci: ${charge.receipt_name}` : "Foto o PDF, max 10 MB"}</span>
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ev-form-receipt" />
            </label>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <button type="button" onClick={() => onClose(false)} className="h-11 rounded-xl border border-white/15 text-slate-200 hover:bg-white/5" data-testid="ev-form-cancel">Annulla</button>
          <button type="button" disabled={busy} onClick={save} className="h-11 rounded-xl btn-gold-primary font-bold disabled:opacity-50" data-testid="ev-form-save">{busy ? "Salvataggio…" : "Salva ricarica"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
